import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { UserStore, UserState } from '../../../store/user.store';

export interface AwardPointsItem {
  type_id: number;
  quantity: number;
  price_per_unit: number;
  label?: string; // za WC fee/meta prikaz (npr. "Privezak sa 2 Fotografije")
}

export interface AwardPointsRequest {
  order_id: string;
  order_total: number;        // RSD products AFTER discount, no delivery (points base — legacy)
  discount_amount?: number;   // info-only
  items?: AwardPointsItem[];  // svi order items (mapped + extras), za WC mirror
  final_total?: number;       // Laravel order.price — autoritativan ukupan iznos za naplatu
  coupon?: string;            // coupon string (za parsiranje popusta na WP)
}

export interface AwardPointsResponse {
  success: boolean;
  order_id?: string;
  order_total?: number;
  multiplier?: number;
  base_points?: number;
  bonus_points?: number;
  points?: number;
  email?: string;
  duplicate?: boolean;
  message?: string;
  wc_order_id?: number;
  redeemed_coupons?: {
    current_level_id: number;
    count: number;
    redeemed_ids: number[];
    redeemed_codes: string[];
  };
}

export interface RefreshUserResponse {
  success: boolean;
  token?: string;
  user?: UserState;
  message?: string;
}

export interface UpdateProfileRequest {
  name?: string;     // "Ime Prezime" — splituje se na first/last na WP-u
  phone?: string;
  address?: string;
  city?: string;
  postcode?: string;
  // NAPOMENA: email se NIKAD ne salje/menja ovde — vezan je za nalog.
}

export interface UpdateProfileResponse {
  success: boolean;
  message?: string;
}

@Injectable({
  providedIn: 'root'
})
export class LoyaltyService {

  private readonly baseUrl = environment.wordpressApiUrl;

  // Last successful award result — purchase-finished page reads this
  // to show "Dobili ste X poena" right after checkout.
  private lastAwardSubject = new BehaviorSubject<AwardPointsResponse | null>(null);
  lastAward$ = this.lastAwardSubject.asObservable();

  constructor(
    private http: HttpClient,
    private userStore: UserStore
  ) {}

  private authHeaders(token: string): HttpHeaders {
    // NOTE: custom header (not Authorization) — the JWT Authentication for WP-API
    // plugin intercepts Authorization headers and rejects our 2-segment SSO token
    // before our route runs. X-Fotoblic-Token bypasses it.
    return new HttpHeaders({
      'X-Fotoblic-Token': token,
      'Content-Type': 'application/json'
    });
  }

  /**
   * Awards WPLoyalty points for a completed order.
   * Server-side: redeems used coupons, creates analytics WC order
   * (which triggers WPLoyalty campaigns: points + email + fresh tier coupon),
   * adds 2x-weekend bonus, idempotent per order_id.
   */
  awardPoints(payload: AwardPointsRequest): Observable<AwardPointsResponse> {
    const token = localStorage.getItem('jwt');

    if (!token) {
      // Guest user / no token — nothing to award, swallow silently.
      return of({ success: false, message: 'No JWT token in localStorage' });
    }

    return this.http
      .post<AwardPointsResponse>(`${this.baseUrl}/fotoblic/v1/award-points`, payload, { headers: this.authHeaders(token) })
      .pipe(
        tap(res => {
          if (res && res.success) {
            this.lastAwardSubject.next(res);
          }
        }),
        catchError(err => {
          // Never break the user's "order finished" flow because of a loyalty failure.
          console.error('[Loyalty] awardPoints failed:', err);
          return of({ success: false, message: err?.error?.message || 'Network error' });
        })
      );
  }

  /**
   * Fetches a fresh user snapshot from WordPress (points, level, discounts)
   * and rotates the localStorage token + UserStore with up-to-date data.
   * Call after every completed order so the app never shows stale state.
   */
  refreshUser(): Observable<RefreshUserResponse> {
    const token = localStorage.getItem('jwt');

    if (!token) {
      return of({ success: false, message: 'No JWT token in localStorage' });
    }

    return this.http
      .post<RefreshUserResponse>(`${this.baseUrl}/fotoblic/v1/me`, {}, { headers: this.authHeaders(token) })
      .pipe(
        tap(res => {
          if (res && res.success && res.token && res.user) {
            localStorage.setItem('jwt', res.token);
            this.userStore.setUser(res.user);
          }
        }),
        catchError(err => {
          console.error('[Loyalty] refreshUser failed:', err);
          return of({ success: false, message: err?.error?.message || 'Network error' });
        })
      );
  }

  /**
   * Updates the WP user's billing profile (name/phone/address/city/postcode).
   * Email is NEVER sent — it stays bound to the account.
   * Server resolves the user from the JWT token, not from the payload.
   */
  updateProfile(payload: UpdateProfileRequest): Observable<UpdateProfileResponse> {
    const token = localStorage.getItem('jwt');

    if (!token) {
      return of({ success: false, message: 'No JWT token in localStorage' });
    }

    // Defanzivno: nikad ne saljemo email, ni ako se slucajno nadje u payloadu.
    const safePayload: UpdateProfileRequest = {
      name:     payload.name,
      phone:    payload.phone,
      address:  payload.address,
      city:     payload.city,
      postcode: payload.postcode
    };

    return this.http
      .post<UpdateProfileResponse>(`${this.baseUrl}/fotoblic/v1/update-profile`, safePayload, { headers: this.authHeaders(token) })
      .pipe(
        catchError(err => {
          console.error('[Loyalty] updateProfile failed:', err);
          return of({ success: false, message: err?.error?.message || 'Network error' });
        })
      );
  }

  getLastAward(): AwardPointsResponse | null {
    return this.lastAwardSubject.getValue();
  }
}
